// src/i18n/index.tsx
//
// Tradução da área do estabelecimento.
//
// A CHAVE É O PRÓPRIO TEXTO EM PORTUGUÊS — mesma convenção do backend, que
// faz $translator->trans('Banner list.'). Com isso:
//
//   - português não precisa de dicionário: a chave já é a tradução;
//   - chave sem tradução aparece em português em vez de aparecer quebrada;
//   - o diff de cada tela é mecânico: Texto  ->  {t("Texto")}.
//
// Uso:
//   const t = useT();
//   <h2>{t("Meus Estabelecimentos")}</h2>
//   <p>{t("Olá, {nome}", { nome: usuario })}</p>

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { DEFAULT_LANG, getLang, setLang as persistirLang } from "./lang";
import type { Lang } from "./lang";
import { en_US } from "./en_US";

export * from "./lang";

type Dicionario = Record<string, string>;

const DICIONARIOS: Record<Lang, Dicionario> = {
  pt_BR: {},
  en_US,
};

export type TFunction = (
  texto: string,
  valores?: Record<string, string | number>,
) => string;

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue>({
  lang: DEFAULT_LANG,
  setLang: () => undefined,
  t: (texto) => texto,
});

/** Troca {chave} pelos valores informados. */
const interpolar = (
  texto: string,
  valores?: Record<string, string | number>,
): string => {
  if (!valores) return texto;

  return Object.keys(valores).reduce(
    (acc, chave) => acc.split(`{${chave}}`).join(String(valores[chave])),
    texto,
  );
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [lang, setLangState] = useState<Lang>(getLang);

  const trocar = useCallback((novo: Lang) => {
    persistirLang(novo);
    setLangState(novo);
  }, []);

  const t = useCallback<TFunction>(
    (texto, valores) => {
      const dicionario = DICIONARIOS[lang] ?? {};
      return interpolar(dicionario[texto] ?? texto, valores);
    },
    [lang],
  );

  const value = useMemo(
    () => ({ lang, setLang: trocar, t }),
    [lang, trocar, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/** Função de tradução. */
export const useT = (): TFunction => useContext(I18nContext).t;

/** Idioma atual e o setter, para o botão de troca. */
export const useLang = () => {
  const { lang, setLang } = useContext(I18nContext);
  return { lang, setLang };
};